import { conferenceLabel } from '../lib/text.js'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import {
  listMyEvents, createMyEvent, patchMyEvent, deleteMyEvent,
  listAdminEvents, createAdminEvent, patchAdminEvent, deleteAdminEvent,
  listSpeakers, listTags, createTag,
} from '../lib/api.js'
import TagPicker from '../components/TagPicker.jsx'
import {
  instantFromVenueLocal, venueLocalFromInstant, isValidTimeZone,
  timeZoneErrorMessage, IANA_TIME_ZONES,
} from '../lib/datetime.js'
import ErrorAlert from '../components/ErrorAlert.jsx'
import useMe from '../hooks/useMe.js'
import { PAGE_STANDARD } from '../lib/layout.js'
import { BTN_PRIMARY, INPUT_BLOCK } from '../lib/ui.js'

const FORMATS = ['IN_PERSON', 'VIRTUAL', 'HYBRID']
const STATUSES = ['DRAFT', 'TENTATIVE', 'PUBLISHED', 'CANCELLED']

// Sentinel for the admin main-speaker <select>: an explicit "no speaker yet"
// choice that maps to `unassignedSpeaker: true` in the payload, distinct from
// the empty "— keep current —" value (which leaves the speaker untouched).
export const NO_SPEAKER = '__none__'


function emptyForm() {
  return {
    title: '',
    abstractText: '',
    startsAtUtc: '',
    endsAtUtc: '',
    tzId: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    format: 'IN_PERSON',
    status: 'DRAFT',
    conference: '', venue: '', address: '',
    lat: '', lng: '',
    city: '', region: '', country: '',
    registrationUrl: '', slidesUrl: '', recordingUrl: '',
    coSpeakerUserIds: [],
    mainSpeakerUserId: '',
    tagSlugs: [],
  }
}

function loadIntoForm(event) {
  const main = (event.speakers || []).find((s) => s.isMain)
  const co = (event.speakers || []).filter((s) => !s.isMain)
  const tzId = event.tzId || 'UTC'
  return {
    title: event.title || '',
    abstractText: event.abstractText || '',
    startsAtUtc: venueLocalFromInstant(event.startsAtUtc, tzId),
    endsAtUtc: venueLocalFromInstant(event.endsAtUtc, tzId),
    tzId,
    format: event.format || 'IN_PERSON',
    status: event.status || 'DRAFT',
    conference: conferenceLabel(event),
    venue: event.venue || '',
    address: event.address || '',
    lat: event.lat ?? '',
    lng: event.lng ?? '',
    city: event.city || '',
    region: event.region || '',
    country: event.country || '',
    registrationUrl: event.registrationUrl || '',
    slidesUrl: event.slidesUrl || '',
    recordingUrl: event.recordingUrl || '',
    coSpeakerUserIds: co.map((s) => s.userId),
    mainSpeakerUserId: main?.userId ?? '',
    tagSlugs: (event.tags || []).map((t) => t.slug),
  }
}

export function buildPayload(form, { isAdmin, isCreate }) {
  const payload = {
    title: form.title.trim(),
    abstractText: form.abstractText.trim() || null,
    startsAtUtc: instantFromVenueLocal(form.startsAtUtc, form.tzId),
    endsAtUtc: instantFromVenueLocal(form.endsAtUtc, form.tzId),
    tzId: form.tzId.trim(),
    format: form.format,
    status: form.status,
    conference: form.conference.trim() || null,
    venue: form.venue.trim() || null,
    address: form.address.trim() || null,
    lat: form.lat === '' ? null : Number(form.lat),
    lng: form.lng === '' ? null : Number(form.lng),
    city: form.city.trim() || null,
    region: form.region.trim() || null,
    country: form.country.trim() || null,
    registrationUrl: form.registrationUrl.trim() || null,
    slidesUrl: form.slidesUrl.trim() || null,
    recordingUrl: form.recordingUrl.trim() || null,
    coSpeakerUserIds: form.coSpeakerUserIds.map((id) => Number(id)).filter(Boolean),
    tagSlugs: form.tagSlugs,
  }
  if (isAdmin) {
    if (form.mainSpeakerUserId === NO_SPEAKER) {
      // Create/leave the event with no main speaker yet (e.g. one we might
      // attend). Wins over mainSpeakerUserId server-side.
      payload.unassignedSpeaker = true
    } else if (form.mainSpeakerUserId) {
      payload.mainSpeakerUserId = Number(form.mainSpeakerUserId)
    }
  }
  // Only POST needs the create-mode default for status; PATCH leaves null
  // fields untouched server-side, so the explicit status above is fine.
  if (!isCreate) {
    // No-op — kept here so the conditional reads symmetrically with create.
  }
  return payload
}

// #379: the zone list moved to lib/datetime.js so the users page shares one
// source with this form. Re-exported because it is part of this module's
// tested surface since #88.
export { IANA_TIME_ZONES }

// #356: returns `{ field, message }` rather than a bare string. The message
// used to surface only as a page-level alert at the top of a long form, which
// tells you something is wrong but not where — on this form the offending field
// can easily be off-screen when you read it. The caller now also marks and
// focuses the field itself.
//
// `field` matches the `name` on the input, which is what wires the two together.
export function validate(form) {
  if (!form.title.trim()) return { field: 'title', message: 'Title is required.' }
  if (!form.startsAtUtc) return { field: 'startsAtUtc', message: 'Start date/time is required.' }
  if (!form.endsAtUtc) return { field: 'endsAtUtc', message: 'End date/time is required.' }
  if (new Date(form.startsAtUtc) >= new Date(form.endsAtUtc)) {
    return { field: 'endsAtUtc', message: 'Start must be before end.' }
  }
  const tz = form.tzId.trim()
  if (!tz) return { field: 'tzId', message: 'Time zone is required.' }
  // #379: `isValidTimeZone` now also rejects the legacy short ids (`EST`,
  // `PST`, …) that Intl resolves and `ZoneId.of` refuses, and the message
  // names the replacement for them.
  if (!isValidTimeZone(tz)) {
    return { field: 'tzId', message: timeZoneErrorMessage(tz) }
  }
  return null
}

export default function EditEventPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { me } = useMe()
  const isCreate = !id || id === 'new'
  const isAdmin = me?.role === 'ADMIN'

  const [form, setForm] = useState(emptyForm)
  const [loading, setLoading] = useState(!isCreate)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [invalidField, setInvalidField] = useState(null)
  // #356: the class for a field that failed validation, plus the message that
  // sits under it. Returns the plain input class for every other field, so
  // adding a field costs one call rather than a conditional each time.
  const fieldClass = (name) =>
    (name === invalidField
      ? `${INPUT_BLOCK} border-rose-400 focus:border-rose-500 focus:ring-rose-200`
      : INPUT_BLOCK)
  const fieldError = (name) =>
    (name === invalidField && error
      ? <p className="mt-1 text-xs font-medium text-rose-700">{error}</p>
      : null)
  const [speakers, setSpeakers] = useState([])
  const [tagsCatalog, setTagsCatalog] = useState([])
  const [pendingTagNames, setPendingTagNames] = useState([])
  const [eventStatusReadOnly, setEventStatusReadOnly] = useState(false)

  // Speaker picker (co-speakers + admin's main-speaker reassignment).
  useEffect(() => {
    listSpeakers().then(setSpeakers).catch(() => setSpeakers([]))
  }, [])

  // Tag catalog for the TagPicker (#116). Refreshed whenever the page
  // mounts; new tags created from this page are POSTed at submit time
  // and don't need to round-trip through this list.
  useEffect(() => {
    listTags().then(setTagsCatalog).catch(() => setTagsCatalog([]))
  }, [])

  // Load existing event for edit mode. Speakers use /me/events (filtered to
  // events they're attached to); admins use /admin/events with
  // includeDeleted=true so soft-deleted rows can be inspected and restored.
  useEffect(() => {
    if (isCreate) return
    if (!me) return // wait for the role to resolve before deciding which endpoint
    setLoading(true)
    setError(null)
    const fetcher = isAdmin
      ? listAdminEvents({ includeDeleted: true, size: 200 }).then((p) => p.content || [])
      : listMyEvents()
    fetcher
      .then((rows) => {
        const match = rows.find((e) => e.id === id)
        if (!match) {
          setError('Event not found, or you no longer have access.')
        } else {
          setForm(loadIntoForm(match))
          const main = (match.speakers || []).find((s) => s.isMain)
          if (!isAdmin && main?.userId !== me?.userId) {
            setEventStatusReadOnly(true)
          }
        }
      })
      .catch((err) => setError(err.message || String(err)))
      .finally(() => setLoading(false))
  }, [id, isCreate, isAdmin, me])

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  // Preserve the underlying UTC instant when the user changes the venue tz —
  // matches the "I picked the wrong city, fix it" mental model. Only re-renders
  // when both old and new tzIds resolve in Intl, otherwise just store the text.
  const onTzIdChange = (nextTz) => {
    setForm((f) => {
      const prev = f.tzId
      if (!isValidTimeZone(prev) || !isValidTimeZone(nextTz)) {
        return { ...f, tzId: nextTz }
      }
      const startUtc = f.startsAtUtc ? instantFromVenueLocal(f.startsAtUtc, prev) : ''
      const endUtc = f.endsAtUtc ? instantFromVenueLocal(f.endsAtUtc, prev) : ''
      return {
        ...f,
        tzId: nextTz,
        startsAtUtc: startUtc ? venueLocalFromInstant(startUtc, nextTz) : f.startsAtUtc,
        endsAtUtc: endUtc ? venueLocalFromInstant(endUtc, nextTz) : f.endsAtUtc,
      }
    })
  }

  const submit = async (e) => {
    e.preventDefault()
    const v = validate(form)
    if (v) {
      setError(v.message)
      setInvalidField(v.field)
      // Take the user to the problem rather than describing it at the top of a
      // form they may have scrolled well past.
      const el = document.querySelector(`[name="${v.field}"]`)
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        el.focus({ preventScroll: true })
      }
      return
    }
    setInvalidField(null)
    setSaving(true)
    setError(null)
    try {
      // #116: POST any newly-typed tag names first so their server-assigned
      // slugs flow into the event upsert. Server is idempotent on slug
      // collision, so duplicates between pending names and the catalog are
      // safely no-ops.
      let resolvedSlugs = form.tagSlugs
      if (pendingTagNames.length) {
        const created = await Promise.all(pendingTagNames.map((name) => createTag(name)))
        const pendingSlugs = created.map((t) => t.slug).filter(Boolean)
        // De-dupe in case the server returned a slug that's already in form.tagSlugs.
        const merged = new Set([...form.tagSlugs, ...pendingSlugs])
        resolvedSlugs = Array.from(merged)
      }
      const payload = buildPayload({ ...form, tagSlugs: resolvedSlugs }, { isAdmin, isCreate })
      const saved = isCreate
        ? (isAdmin ? await createAdminEvent(payload) : await createMyEvent(payload))
        : (isAdmin ? await patchAdminEvent(id, payload) : await patchMyEvent(id, payload))
      setPendingTagNames([])
      navigate(`/admin/events/${saved.id}`, { replace: true })
    } catch (err) {
      setError(err.message || String(err))
    } finally {
      setSaving(false)
    }
  }

  const onSoftDelete = async () => {
    if (!window.confirm('Soft-delete this event? It can be restored by an admin.')) return
    setSaving(true)
    try {
      if (isAdmin) await deleteAdminEvent(id)
      else await deleteMyEvent(id)
      navigate('/admin/events', { replace: true })
    } catch (err) {
      setError(err.message || String(err))
    } finally {
      setSaving(false)
    }
  }

  const co = useMemo(() => new Set(form.coSpeakerUserIds.map(Number)), [form.coSpeakerUserIds])
  const toggleCo = (userId) => {
    const next = new Set(co)
    if (next.has(userId)) next.delete(userId)
    else next.add(userId)
    set({ coSpeakerUserIds: Array.from(next) })
  }

  if (loading) {
    return (
      <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
        <div className="rounded-2xl bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200/60">
          Loading…
        </div>
      </main>
    )
  }

  return (
    <main className={`${PAGE_STANDARD} pb-16 pt-8 sm:pt-10`}>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            <span className="brand-gradient-text">{isCreate ? 'New event' : 'Edit event'}</span>
          </h1>
          {!isCreate ? (
            <p className="mt-2 text-sm text-slate-500">
              Editing as <span className="font-medium text-slate-700">{me?.role}</span>.
            </p>
          ) : null}
        </div>
        <Link to="/admin/events" className="text-sm text-slate-600 hover:text-brand-700">← My events</Link>
      </header>

      {error ? <ErrorAlert title="Save failed" message={error} source="EditEventPage" /> : null}

      <datalist id="iana-tz-list">
        {IANA_TIME_ZONES.map((tz) => <option key={tz} value={tz} />)}
      </datalist>

      <form onSubmit={submit} className="space-y-6 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/60 sm:p-8">
        <fieldset disabled={eventStatusReadOnly} className="space-y-4">
          <label className="block text-sm">
            <span className="block font-medium text-slate-700">Title</span>
            <input
              name="title"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              className={fieldClass('title')}
              aria-invalid={invalidField === 'title'}
              required
            />
            {fieldError('title')}
          </label>

          <label className="block text-sm">
            <span className="block font-medium text-slate-700">Abstract</span>
            <textarea
              value={form.abstractText}
              onChange={(e) => set({ abstractText: e.target.value })}
              rows={6}
              className={INPUT_BLOCK}
              placeholder="What's the talk about? (Markdown allowed; renders as plain text on the public page.)"
            />
          </label>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Start (venue local time)</span>
              <input
                type="datetime-local"
                name="startsAtUtc"
                value={form.startsAtUtc}
                onChange={(e) => set({ startsAtUtc: e.target.value })}
                className={fieldClass('startsAtUtc')}
                aria-invalid={invalidField === 'startsAtUtc'}
                step="300"
                required
              />
              {fieldError('startsAtUtc')}
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">End (venue local time)</span>
              <input
                type="datetime-local"
                name="endsAtUtc"
                value={form.endsAtUtc}
                onChange={(e) => set({ endsAtUtc: e.target.value })}
                className={fieldClass('endsAtUtc')}
                aria-invalid={invalidField === 'endsAtUtc'}
                step="300"
                required
              />
              {fieldError('endsAtUtc')}
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Time zone (IANA)</span>
              <input
                name="tzId"
                value={form.tzId}
                onChange={(e) => onTzIdChange(e.target.value)}
                className={fieldClass('tzId')}
                aria-invalid={invalidField === 'tzId'}
                placeholder="America/New_York"
                list="iana-tz-list"
                required
              />
              {fieldError('tzId')}
            </label>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Format</span>
              <select value={form.format} onChange={(e) => set({ format: e.target.value })} className={INPUT_BLOCK}>
                {FORMATS.map((f) => <option key={f} value={f}>{f.replace('_', '-').toLowerCase()}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Status</span>
              <select value={form.status} onChange={(e) => set({ status: e.target.value })} className={INPUT_BLOCK}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Conference</span>
              <input
                value={form.conference}
                onChange={(e) => set({ conference: e.target.value })}
                className={INPUT_BLOCK}
                placeholder="DGIQ West, Databricks Summit, …"
              />
            </label>
          </div>

          <div>
            <span className="block text-sm font-medium text-slate-700">Tags</span>
            <p className="text-xs text-slate-500 mb-2">
              Up to 10. Type to search; press Enter (or click Create) to add a new tag.
            </p>
            <TagPicker
              value={form.tagSlugs}
              onChange={(slugs) => set({ tagSlugs: slugs })}
              pendingNames={pendingTagNames}
              onPendingChange={setPendingTagNames}
              availableTags={tagsCatalog}
              maxTags={10}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Venue</span>
              <input value={form.venue} onChange={(e) => set({ venue: e.target.value })} className={INPUT_BLOCK} />
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Address</span>
              <input value={form.address} onChange={(e) => set({ address: e.target.value })} className={INPUT_BLOCK} />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">City</span>
              <input value={form.city} onChange={(e) => set({ city: e.target.value })} className={INPUT_BLOCK} />
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Region</span>
              <input value={form.region} onChange={(e) => set({ region: e.target.value })} className={INPUT_BLOCK} />
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Country</span>
              <input value={form.country} onChange={(e) => set({ country: e.target.value })} className={INPUT_BLOCK} placeholder="US" />
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Lat</span>
              <input type="number" step="any" value={form.lat} onChange={(e) => set({ lat: e.target.value })} className={INPUT_BLOCK} />
            </label>
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Lng</span>
              <input type="number" step="any" value={form.lng} onChange={(e) => set({ lng: e.target.value })} className={INPUT_BLOCK} />
            </label>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="block text-sm sm:col-span-1">
              <span className="block font-medium text-slate-700">Registration URL</span>
              <input value={form.registrationUrl} onChange={(e) => set({ registrationUrl: e.target.value })} className={INPUT_BLOCK} />
            </label>
            <label className="block text-sm sm:col-span-1">
              <span className="block font-medium text-slate-700">Slides URL</span>
              <input value={form.slidesUrl} onChange={(e) => set({ slidesUrl: e.target.value })} className={INPUT_BLOCK} />
            </label>
            <label className="block text-sm sm:col-span-1">
              <span className="block font-medium text-slate-700">Recording URL</span>
              <input value={form.recordingUrl} onChange={(e) => set({ recordingUrl: e.target.value })} className={INPUT_BLOCK} />
            </label>
          </div>

          {isAdmin ? (
            <label className="block text-sm">
              <span className="block font-medium text-slate-700">Main speaker (admin override)</span>
              <select
                value={form.mainSpeakerUserId || ''}
                onChange={(e) => set({ mainSpeakerUserId: e.target.value })}
                className={INPUT_BLOCK}
              >
                <option value="">— keep current —</option>
                <option value={NO_SPEAKER}>— No speaker yet —</option>
                {speakers.map((s) => (
                  <option key={s.userId} value={s.userId}>
                    {s.displayName || s.email || `User #${s.userId}`}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <fieldset className="rounded-xl border border-slate-200 p-3">
            <legend className="px-1 text-sm font-medium text-slate-700">Co-speakers</legend>
            {speakers.length === 0 ? (
              <p className="text-sm text-slate-500">No other group members yet.</p>
            ) : (
              <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                {speakers
                  .filter((s) => Number(s.userId) !== Number(form.mainSpeakerUserId || me?.userId))
                  .map((s) => (
                    <li key={s.userId}>
                      <label className="inline-flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={co.has(Number(s.userId))}
                          onChange={() => toggleCo(Number(s.userId))}
                        />
                        <span>{s.displayName || s.email || `User #${s.userId}`}</span>
                      </label>
                    </li>
                  ))}
              </ul>
            )}
          </fieldset>
        </fieldset>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={saving || eventStatusReadOnly}
              className={BTN_PRIMARY}
            >
              {saving ? 'Saving…' : isCreate ? 'Create event' : 'Save changes'}
            </button>
            <Link
              to="/admin/events"
              className="inline-flex min-h-[44px] items-center rounded-full px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
            >
              Cancel
            </Link>
          </div>
          {!isCreate && !eventStatusReadOnly ? (
            <button
              type="button"
              onClick={onSoftDelete}
              disabled={saving}
              className="inline-flex min-h-[44px] items-center rounded-full px-4 py-2 text-sm font-medium text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50"
            >
              Soft-delete
            </button>
          ) : null}
        </div>
      </form>
    </main>
  )
}
